import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useSetAtom } from 'jotai/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDebounce } from '@node-space/hooks'
import { TextAction } from '@node-space/storybook-components'
import Callout from '@node-space/storybook-components/dist/Callout'
import Icon from '@node-space/storybook-components/dist/Icon'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import Search from '@node-space/storybook-components/dist/Search'
import SidePanel, { useSidePanel } from '@node-space/storybook-components/dist/SidePanel'
import SimpleTableV2 from '@node-space/storybook-components/dist/SimpleTableV2'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { Text } from '@node-space/storybook-components/dist/Text'
import {
  Box,
  Button,
  Box as Container,
  Box as CustomerContainter,
  Box as CustomerInfoContainer,
  Sheet,
} from '@node-space/ui-components'
import { capitalize } from '@node-space/utils'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import TablePagination from 'components/PageableTablePagination'
import { Description } from 'components/text/Description'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { TransactionExportModal } from 'pages/WalletDetails/components/RecentActivity/WalletTransactionsExport/TransactionExportModal'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import { walletActionAtom } from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { reactQueryKeys, reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { CustomerStatus, CustomerWallet } from 'types/customers'
import { PaginationPaging } from 'types/pagination'
import { ActionIds, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { highlightRowBasedOnId } from 'utils/table/table'
import track from 'utils/tracker'
import { currentCustomerAtom } from './atoms/customersAtoms'
import { AddWallet } from './components/AddWallet'
import CustomerDepositForm from './components/CustomerDeposit/CustomerDeposit'
import CustomerInfo from './components/CustomerInfo'
import CustomerWalletSidepanelDetails from './components/CustomerWallet/CustomerWalletSidepanelDetails'
import CustomerWalletSidePanelHeader from './components/CustomerWallet/CustomerWalletSidePanelHeader'
import CustomerWalletsSkeletonLoader from './components/CustomerWallet/CustomerWalletsSkeletonLoader'
import { useCustomerWalletsTableRowData } from './hooks/useCustomerWalletsTableRowData'
import { CustomerDetails } from './types'
import { isBusinessCustomer } from './utils'

const rowHeaderWidth = 'px-8 w-1/3.5'

export const Customer = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { id }: { id?: number } = useParams()
  const permissions = useMappedRoles()?.wallets
  const canTrade = permissions?.canTrade
  const canEdit = permissions?.canEdit
  const { viewOnly: isViewOnlyRole, profile } = useProfileContext() || {}
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(null)
  const [isPayWalletModalOpen, setIsPayWalletModalOpen] = useState(false)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchTerm = useDebounce(searchQuery, 500)
  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)
  const setWalletAction = useSetAtom(walletActionAtom)
  const defaultCustomerDetails: CustomerDetails = {
    type: '',
    registrationNumber: '',
    taxResidence: '',
    address: '',
    name: '',
    representativeName: '',
  }

  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>(defaultCustomerDetails)
  const [isAddWalletSheetOpen, setIsAddWalletSheetOpen] = useState<boolean>(false)
  const [isMoveSheetOpen, setIsMoveSheetOpen] = useState<boolean>(false)
  const [isDepositFormOpen, setIsDepositFormOpen] = useState<boolean>(false)
  const [currentCustomerWallet, setCurrentCustomerWallet] = useState<CustomerWallet>()
  const [page, setPage] = useState<number>(0)
  const [isTransactionExportModalVisible, setIsTransactionExportModalVisible] = useState(false)

  const {
    data: customer,
    error,
    isFetching: isFetchingCustomerInfo,
  } = useCustomerQuery(id?.toString())

  const {
    data: { content: customerWallets, pagination: customerWalletPagination } = {},
    isFetching: isFetchingCustomerWallets,
    error: customerWalletsErrors,
    refetch: fetchCustomerWallets,
  } = useGetCustomersWalletsQuery({
    customerReference: customer?.reference,
    page,
    currencyCodeSearch: debouncedSearchTerm,
  })

  const { paging, showPagination } = usePaginationPagingPersist(customerWalletPagination)

  useEffect(() => {
    customer?.reference && fetchCustomerWallets()
  }, [debouncedSearchTerm, customer?.reference])

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.CUSTOMER, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })

    setCurrentCustomerWallet(null)
  }, [])

  const {
    data: countries,
    isPending: isCountriesLoading,
    isError: isCountriesLoadingError,
  } = useCountriesQuery(true)

  const taxResidencyCountry = useMemo(() => {
    return countries?.find(country => {
      if (
        isBusinessCustomer(customer) &&
        country?.code === customer?.company?.taxResidenceCountryCode
      ) {
        return country
      }
    })
  }, [countries, customer])

  useEffect(() => {
    if (customer && isBusinessCustomer(customer)) {
      const details: CustomerDetails = {
        type: customer?.type ? capitalize(customer?.type?.toLowerCase()) : '',
        registrationNumber: customer?.company?.registrationNumber ?? '',
        taxResidence: taxResidencyCountry?.name ?? '',
        address: [
          customer?.company?.address?.addressLine1,
          customer?.company?.address?.addressLine2,
          customer?.company?.address?.city,
          customer?.company?.address?.country,
        ]
          ?.filter(Boolean)
          ?.join(', '),
        name: customer?.company?.name ?? '',
        representativeName: `${customer?.company?.representative?.firstName ?? ''} ${customer?.company?.representative?.lastName ?? ''}`,
      }
      setCustomerDetails(details)
    }
  }, [customer, taxResidencyCountry])

  useEffect(() => {
    setCurrentCustomerAtom(customer)
  }, [customer])

  const handleOnAddCustomerWallet = () => {
    setIsAddWalletSheetOpen(true)
  }

  const handleAddWalletSheetClose = () => {
    setIsAddWalletSheetOpen(false)
  }
  const {
    isSidePanelOpen: isCustomerWalletSidePanelOpen,
    sidePanelData: customerWalletData,
    paging: pagingCustomerWalletPaging,
    setIsSidePanelOpen: setIsCustomerWalletSidePanelOpen,
    updateSidePanel: updateCustomerWalletSidePanel,
  } = useSidePanel(customerWallets)

  const handleOnDetailsClick = (index: number) => {
    updateCustomerWalletSidePanel(index)
    setIsCustomerWalletSidePanelOpen(true)
  }

  const handleOnDepositClick = (customerWallet: CustomerWallet) => {
    setCurrentCustomerWallet(customerWallet)
    setIsDepositFormOpen(true)
  }

  const handleOpenMoveSheet = (wallet: CustomerWallet) => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsMoveSheetOpen(false)
        },
        moveMeta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: wallet,
        },
      })
    }
    setIsMoveSheetOpen(true)
  }

  const handleOnPayClick = (wallet: CustomerWallet) => {
    const walletAction: WalletAction = {
      key: 'pay',
      data: {
        id: wallet?.id,
      },
      metadata: {
        pay: {
          beneficiaryPaymentType: PayActionBeneficiaryTypes.UNDERLYING_CUSTOMER,
          customerReference: wallet?.customerReference,
        },
      },
    }

    setWalletModalAction(walletAction)
    setIsPayWalletModalOpen(true)
  }

  const handleExportTransactionClick = (customerWallet: CustomerWallet) => {
    setCurrentCustomerWallet(customerWallet)
    setIsTransactionExportModalVisible(true)
  }

  const handleOnExportModalClose = () => {
    setIsTransactionExportModalVisible(false)
    setCurrentCustomerWallet(null)
  }

  const isActiveCustomer = customer?.status === CustomerStatus.VERIFIED
  const tableRows = useCustomerWalletsTableRowData({
    customerWallets,
    isActiveCustomer,
    handleExportTransactionClick,
    handleOnDetailsClick,
    handleOnDepositClick,
    handleOpenMoveSheet,
    handleOnPayClick,
  })

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
    await queryClient.invalidateQueries({
      queryKey: [reactQueryName.CUSTOMER_WALLETS],
    })
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
    })
  }

  const clearSearchCriteria = () => {
    setSearchQuery('')
  }

  const isAddCustomerWalletButtonVisible = (canEdit || canTrade) && isActiveCustomer

  const isSearchVisible = !!tableRows?.length || !(!tableRows?.length && !debouncedSearchTerm)

  const CustomerContent = useMemo(() => {
    if (isFetchingCustomerInfo || isFetchingCustomerWallets) {
      return (
        <Box width="full" role="skeletonTab">
          <CustomerWalletsSkeletonLoader />
        </Box>
      )
    } else if (customerWalletsErrors) {
      return (
        <StatusCard status="error" headline={t('defaultError')}>
          <Description>
            {`${t('requestErrors.comeBackLater')} `}
            <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
          </Description>
        </StatusCard>
      )
    } else {
      return (
        <>
          {isSearchVisible && (
            <Box paddingB={16} width="full">
              <Search
                searchTerm={searchQuery}
                size="sm"
                testid="customer-wallet-search-field"
                placeholderText={t('customers.customerWalletSearchPlaceholder')}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </Box>
          )}

          {!isActiveCustomer && customerWallets?.length && (
            <Box paddingB={16}>
              <Callout state="info" message={t('customers.inactiveCustomerInfoMessage')} />
            </Box>
          )}

          {!isActiveCustomer && !customerWallets?.length && (
            <StatusCard headline={null} description={t('customers.inactiveCustomerMessage')} />
          )}

          {!tableRows?.length &&
            !customerWallets?.length &&
            !debouncedSearchTerm &&
            isActiveCustomer && (
              <StatusCard
                cardButton={
                  isAddCustomerWalletButtonVisible && {
                    onClick: handleOnAddCustomerWallet,
                    name: t('customers.addWallet'),
                    iconElement: <Icon name="PlusIcon" size="default" color="white" />,
                    disabled: isViewOnlyRole,
                  }
                }
                description={t('customers.customerWalletDescription', {
                  customerName: customerDetails?.name,
                })}
                headline={t('customers.noCustomerWalletHeadline', {
                  customerName: customerDetails?.name,
                })}
              />
            )}

          {!tableRows?.length && debouncedSearchTerm && (
            <NoSearchResults clearSearchCriteria={clearSearchCriteria} />
          )}

          {!!tableRows?.length && (
            <SimpleTableV2
              isLoading={isFetchingCustomerWallets}
              columns={[
                { headerTitle: t('walletName'), className: 'pl-4' },
                { headerTitle: '' },
                { headerTitle: '' },
                {
                  headerTitle: '',
                  className: rowHeaderWidth,
                  align: 'end',
                },
              ]}
              tableData={tableRows}
            />
          )}
        </>
      )
    }
  }, [
    isFetchingCustomerWallets,
    customerWalletsErrors,
    tableRows,
    searchQuery,
    page,
    isFetchingCustomerInfo,
    debouncedSearchTerm,
    isSearchVisible,
    customerWallets,
  ])

  return (
    <>
      <PageHeader
        description={
          <Text size="sm" color="grey">
            {t('customers.customerDescription', { customerName: customerDetails?.name })}
          </Text>
        }
        renderRightSection={
          isAddCustomerWalletButtonVisible && (
            <Button
              onClick={handleOnAddCustomerWallet}
              iconElement={<Icon name="PlusIcon" color="inherit" />}
              disabled={isViewOnlyRole}
            >
              {t('customers.addWallet')}
            </Button>
          )
        }
      />
      <Container flex className="md:flex-row" flexCol gap={24} gapY={24} width="full">
        <CustomerContainter flex flexCol width="full" className="md:w-2/3 break-words">
          {CustomerContent}

          {!!paging && !showPagination && (
            <Box
              className={clsx({
                'opacity-25': isFetchingCustomerWallets || showPagination,
              })}
            >
              <TablePagination
                disableLeftDropdown
                total={paging?.total}
                pagingMax={paging?.max}
                onPagination={({ page }: PaginationPaging) => setPage(page)}
                resetPageNumber={debouncedSearchTerm}
              />
            </Box>
          )}
          <SidePanel
            isOpen={isCustomerWalletSidePanelOpen}
            onClose={() => setIsCustomerWalletSidePanelOpen(false)}
            paging={pagingCustomerWalletPaging}
            scrollBody={false}
            activeRowId={customerWalletData?.id}
            onActiveRowChange={highlightRowBasedOnId}
            sidePanelHeader={{
              customSidePanelHeader: (
                <CustomerWalletSidePanelHeader customerWalletData={customerWalletData} />
              ),
            }}
          >
            <Container flex flexCol paddingX={20} paddingY={24} gapY={12}>
              <CustomerWalletSidepanelDetails customerWalletData={customerWalletData} />
            </Container>
          </SidePanel>
        </CustomerContainter>
        {customerDetails && (
          <CustomerInfoContainer flexCol className="md:w-1/3 xxs:flex-row ">
            <CustomerInfo customerDetails={customerDetails} isLoading={isFetchingCustomerInfo} />
          </CustomerInfoContainer>
        )}
      </Container>

      <Sheet
        isOpen={isAddWalletSheetOpen}
        header={t('customers.newWallet')}
        setIsOpen={setIsAddWalletSheetOpen}
        onClose={handleAddWalletSheetClose}
      >
        <AddWallet walletType={WalletTypes?.CUSTOMER} handleClose={handleAddWalletSheetClose} />
      </Sheet>

      <CustomerDepositForm
        customerDetails={customerDetails}
        initialCustomerWallet={currentCustomerWallet}
        isVisable={isDepositFormOpen}
        onModalClose={() => setIsDepositFormOpen(false)}
      />

      <WalletSheet isSheetOpen={isMoveSheetOpen} setIsSheetOpen={setIsMoveSheetOpen} />

      {isPayWalletModalOpen && (
        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={refreshWallets}
          setShowWalletModal={setIsPayWalletModalOpen}
        />
      )}

      {isTransactionExportModalVisible && (
        <TransactionExportModal
          key={currentCustomerWallet?.id}
          handleClose={handleOnExportModalClose}
          showCustomDateExportModal={isTransactionExportModalVisible}
          downloadAll={false}
          customerWallet={currentCustomerWallet}
        />
      )}
    </>
  )
}
